import React, { useEffect, useState, useRef, useCallback } from 'react';
import './VideoControlsBar.css';

export default function VideoControlsBar({
  videoRef,
  onPlay,
  onPause,
  isFullscreen,
  onToggleFullscreen,
  currentTime,
  setCurrentTime,
  duration,
  isPlaying,
  volume,
  onVolumeChange,
  onMuteToggle,
  playbackRate,
  onRateChange,
  showFsNotes,
  onToggleNotes,
  isMuted,
  onCaptureScreenshot,
  onAboutToToggleFullscreen,
  showSkipButtons = false,
  skipSeconds = 10,
  showTrimButton = false,
  onTrimButtonClick,
  trimButtonLabel = 'Trim',
  trimButtonActive = false,

  // ✅ NEW (auto-hide)
  uiHidden = false,
  onUserActivity,
  className = '',

  // ✅ NEW (POV)
  povOptions = [],        // [{ id, title }]
  activePovId = null,     // current videoId
  onSelectPov,            // (nextVideoId) => void
}) {
  const [pendingSeek, setPendingSeek] = useState(false);

  const [seeking, setSeeking] = useState(false);
  const [seekVal, setSeekVal] = useState(0);
  const [localVol, setLocalVol] = useState(volume ?? 1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const singleClickTimerRef = useRef(null);

  // ✅ NEW (POV)
  const [showPovMenu, setShowPovMenu] = useState(false);
  const povWrapRef = useRef(null);

  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const settingsWrapRef = useRef(null);

  const [skipToast, setSkipToast] = useState(null); 
  // { side: 'left'|'right', delta: -10|10, key: number }

  const showSkipToast = (side, delta) => {
    setSkipToast({ side, delta, key: Date.now() });
    window.clearTimeout(showSkipToast._t);
    showSkipToast._t = window.setTimeout(() => setSkipToast(null), 450);
  };

  useEffect(() => {
  const onDown = (e) => {
    if (!showSettingsMenu) return;
    if (settingsWrapRef.current && !settingsWrapRef.current.contains(e.target)) {
      setShowSettingsMenu(false);
    }
  };
  document.addEventListener('mousedown', onDown);
  return () => document.removeEventListener('mousedown', onDown);
}, [showSettingsMenu]);

  useEffect(() => { setLocalVol(volume ?? 1); }, [volume]);
  useEffect(() => {
    if (!seeking && !pendingSeek) {
      setSeekVal(currentTime || 0);
    }
  }, [currentTime, seeking, pendingSeek]);

  const timeRef = useRef(currentTime || 0);
  const durRef  = useRef(duration || 0);
  const volRef  = useRef(localVol ?? 1);
  const playingRef = useRef(!!isPlaying);
  const fsRef = useRef(!!isFullscreen);

  const povClickTimerRef = useRef(null);

  useEffect(() => { playingRef.current = !!isPlaying; }, [isPlaying]);
  useEffect(() => { fsRef.current = !!isFullscreen; }, [isFullscreen]);
  useEffect(() => { timeRef.current = currentTime || 0; }, [currentTime]);
  useEffect(() => { durRef.current  = duration || 0; }, [duration]);
  useEffect(() => { volRef.current  = (volume ?? localVol ?? 1); }, [volume, localVol]);

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  const isTypingContext = (el) => {
    if (!el) return false;
    const tag = el.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea') return true;
    if (el.isContentEditable) return true;
    if (tag === 'input' && el.type === 'range') return true;
    return false;
  };

  const rootRef = useRef(null);
  const focusRoot = useCallback(() => {
    requestAnimationFrame(() => rootRef.current?.focus());
  }, []);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const updateBarHeightVar = () => {
      const h = el.getBoundingClientRect().height;
      el.style.setProperty('--vcb-bar-height', `${h}px`);
    };

    updateBarHeightVar();

    const ro = new ResizeObserver(() => updateBarHeightVar());
    ro.observe(el);

    window.addEventListener('resize', updateBarHeightVar);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateBarHeightVar);
    };
  }, []);

  // ✅ close POV menu on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (!showPovMenu) return;
      if (povWrapRef.current && !povWrapRef.current.contains(e.target)) {
        setShowPovMenu(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showPovMenu]);

  // Detect coarse (mobile) vs fine (desktop) pointer
  const isCoarsePointerRef = useRef(
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(pointer: coarse)').matches
  );
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(pointer: coarse)');
    const handler = (e) => { isCoarsePointerRef.current = e.matches; };
    mq.addEventListener?.('change', handler);
    mq.addListener?.(handler); // Safari fallback
    return () => {
      mq.removeEventListener?.('change', handler);
      mq.removeListener?.(handler);
    };
  }, []);

  // Key handling on the controls root (when controls have focus)
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const onKeyDown = (e) => {
      if (isTypingContext(e.target)) return;

      const k = (e.key || '').toLowerCase();
      const code = e.code || '';

      const isLeft  = e.key === 'ArrowLeft'  || k === 'uikeyinputleftarrow'  || code === 'ArrowLeft';
      const isRight = e.key === 'ArrowRight' || k === 'uikeyinputrightarrow' || code === 'ArrowRight';
      const isUp    = e.key === 'ArrowUp'    || k === 'uikeyinputuparrow'    || code === 'ArrowUp';
      const isDown  = e.key === 'ArrowDown'  || k === 'uikeyinputdownarrow'  || code === 'ArrowDown';

      if (k === ' ' || code === 'Space') {
        e.preventDefault();
        if (playingRef.current) onPause?.(); else onPlay?.();
        return;
      }
      if (k === 'm') { e.preventDefault(); onMuteToggle?.(); return; }
      if (k === 's') { e.preventDefault(); onCaptureScreenshot?.(); return; }
      if (k === 'n' && fsRef.current) { e.preventDefault(); onToggleNotes?.(); return; }

      // YouTube-style hotkeys
      if (k === 'k' || code === 'KeyK') {
        e.preventDefault();
        if (playingRef.current) onPause?.(); else onPlay?.();
        return;
      }
      if (k === 'j' || code === 'KeyJ') {
        e.preventDefault();
        const next = clamp(timeRef.current - 10, 0, durRef.current || Infinity);
        setCurrentTime(next);
        return;
      }
      if (k === 'l' || code === 'KeyL') {
        e.preventDefault();
        const next = clamp(timeRef.current + 10, 0, durRef.current || Infinity);
        setCurrentTime(next);
        return;
      }

      if (isLeft || isRight) {
        const delta = isRight ? 10 : -10;
        const next = clamp(timeRef.current + delta, 0, durRef.current || Infinity);
        setCurrentTime(next);
        e.preventDefault();
        return;
      }

      if (isUp || isDown) {
        const step = 0.05;
        const next = clamp(volRef.current + (isUp ? step : -step), 0, 1);
        setLocalVol(next);
        onVolumeChange(next);
        e.preventDefault();
        return;
      }
    };

    el.addEventListener('keydown', onKeyDown, { passive: false });
    return () => el.removeEventListener('keydown', onKeyDown);
  }, [setCurrentTime, onVolumeChange, onPlay, onPause, onMuteToggle, onToggleNotes, onCaptureScreenshot]);

  // Global key handler so hotkeys work even when controls aren't focused (incl. fullscreen)
  useEffect(() => {
    const onKeyDown = (e) => {
      if (rootRef.current && rootRef.current.contains(e.target)) return;
      if (isTypingContext(e.target)) return;

      const k = (e.key || '').toLowerCase();
      const code = e.code || '';

      const consume = () => {
        e.preventDefault?.();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        e.stopPropagation?.();
      };

      if (k === ' ' || code === 'Space') {
        consume();
        if (playingRef.current) onPause?.(); else onPlay?.();
        return;
      }
      if (k === 'm') { consume(); onMuteToggle?.(); return; }
      if (k === 's') { consume(); onCaptureScreenshot?.(); return; }
      if (k === 'n' && fsRef.current) { consume(); onToggleNotes?.(); return; }
      if (k === 'escape' && fsRef.current) { consume(); onToggleFullscreen?.(); return; }

      // YouTube-style hotkeys (global)
      if (k === 'k' || code === 'KeyK') {
        consume();
        if (playingRef.current) onPause?.(); else onPlay?.();
        return;
      }
      if (k === 'j' || code === 'KeyJ') {
        consume();
        const next = clamp(timeRef.current - 10, 0, durRef.current || Infinity);
        setCurrentTime(next);
        return;
      }
      if (k === 'l' || code === 'KeyL') {
        consume();
        const next = clamp(timeRef.current + 10, 0, durRef.current || Infinity);
        setCurrentTime(next);
        return;
      }
    };

    const captureOpts = { capture: true };
    document.addEventListener('keydown', onKeyDown, captureOpts);
    return () => document.removeEventListener('keydown', onKeyDown, captureOpts);
  }, [onPlay, onPause, onMuteToggle, onCaptureScreenshot, onToggleNotes, onToggleFullscreen, setCurrentTime]);

  useEffect(() => {
    const onFsChange = () => {
      requestAnimationFrame(() => rootRef.current?.focus());
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  useEffect(() => {
    return () => {
      if (povClickTimerRef.current) clearTimeout(povClickTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!pendingSeek) return;

    if (Math.abs((currentTime || 0) - seekVal) < 0.3) {
      setPendingSeek(false);
    }
  }, [currentTime, seekVal, pendingSeek]);

  // Mobile gestures + desktop dbl-click fullscreen
  const lastTapRef = useRef({ side: null, t: 0 });
  const doSeek = (delta, side = null, showFx = false) => {
    const next = clamp((timeRef.current || 0) + delta, 0, durRef.current || Infinity);
    setCurrentTime(next);
    if (showFx && side) showSkipToast(side, delta);
  };
  const handleDoubleTap = (e, side) => {
    e.preventDefault();
    if (isCoarsePointerRef.current) {
      doSeek(side === 'right' ? 10 : -10, side, true);
    } else {
      handleToggleFullscreen();
    }
  };

  const handleTap = (e, side) => {
    const now = Date.now();
    const { side: lastSide, t } = lastTapRef.current;
    if (isCoarsePointerRef.current) {
      if (lastSide === side && now - t < 300) {
        doSeek(side === 'right' ? 10 : -10, side, true);
      } else  {
        if (playingRef.current) onPause?.(); else onPlay?.();
      }
      lastTapRef.current = { side, t: now };
    } else {
      if (playingRef.current) onPause?.(); else onPlay?.();
      lastTapRef.current = { side, t: now };
    }
  };

  // Swipe seeking (mobile)
  const swipeRef = useRef({ startX: 0, baseTime: 0, active: false });
  const onTouchStart = (e) => {
    if (!e.touches?.[0]) return;
    swipeRef.current = {
      startX: e.touches[0].clientX,
      baseTime: timeRef.current || 0,
      active: true
    };
  };
  const onTouchMove = (e) => {
    if (!swipeRef.current.active || !e.touches?.[0]) return;
    const dx = e.touches[0].clientX - swipeRef.current.startX;
    const delta = dx / 12;
    const next = clamp(swipeRef.current.baseTime + delta, 0, durRef.current || Infinity);
    setCurrentTime(next);
  };
  const onTouchEnd = () => { swipeRef.current.active = false; };

  // Desktop click/double-click helpers (if you use the gesture layer surface)
  const handleDesktopSingleClick = (e) => {
    if (isCoarsePointerRef.current) return;
    if (e.target !== e.currentTarget) return;
    if (e.defaultPrevented) return;

    clearTimeout(singleClickTimerRef.current);
    singleClickTimerRef.current = setTimeout(() => {
      if (playingRef.current) onPause?.(); else onPlay?.();
    }, 200);
  };

  const handleDesktopDoubleClick = (e) => {
    if (!isCoarsePointerRef.current) {
      e.preventDefault();
      clearTimeout(singleClickTimerRef.current);
      handleToggleFullscreen();
    }
  };

  // Delegate fullscreen to parent only
  const handleToggleFullscreen = () => {
    onAboutToToggleFullscreen?.();
    onToggleFullscreen?.();
  };

  const format = (s = 0) => {
    if (!Number.isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec < 10 ? '0' : ''}${sec}`;
  };

  const onProgressInput = (e) => { setSeekVal(Number(e.target.value)); };
  const onProgressCommit = () => {
    setSeeking(false);
    setPendingSeek(true);
    setCurrentTime(seekVal);
  };
  const onVolumeInput = (e) => {
    const v = Number(e.target.value);
    setLocalVol(v);
    onVolumeChange(v);
  };

  const rates = [0.5, 0.75, 1, 1.25, 1.5, 2];

  const hasPov = Array.isArray(povOptions) && povOptions.length > 1;
  const hasCaptureAction = typeof onCaptureScreenshot === 'function';
  const showInlineNotesButton = Boolean(isFullscreen && typeof onToggleNotes === 'function');
  const showCornerTools = hasCaptureAction || showInlineNotesButton;

  const cycleToNextPov = useCallback(() => {
    if (!hasPov) return;

    const ids = povOptions.map((p) => p.id);
    const idx = Math.max(0, ids.indexOf(activePovId));
    const nextIdx = (idx + 1) % ids.length;
    const nextId = ids[nextIdx];

    if (nextId && nextId !== activePovId) {
      onSelectPov?.(nextId);
    }
  }, [hasPov, povOptions, activePovId, onSelectPov]);


  return (
    <div
      className={`vcb-gesture-layer ${uiHidden ? 'vcb-ui-hidden' : ''} ${className}`}
      onPointerDown={(e) => { e.stopPropagation(); }}
      onTouchStart={(e) => { e.stopPropagation(); onUserActivity?.(); onTouchStart(e); }}
      onTouchMove={(e) => { e.stopPropagation(); onUserActivity?.(); onTouchMove(e); }}
      onTouchEnd={(e) => { e.stopPropagation(); onUserActivity?.(); onTouchEnd(e); }}
      onMouseMove={(e) => { e.stopPropagation(); onUserActivity?.(); }}
      onPointerMove={(e) => { e.stopPropagation(); onUserActivity?.(); }}
      onClick={(e) => { e.stopPropagation(); onUserActivity?.(); handleDesktopSingleClick(e); }}
      onDoubleClick={(e) => { e.stopPropagation(); onUserActivity?.(); handleDesktopDoubleClick(e); }}
    >
      {skipToast && (
        <div
          key={skipToast.key}
          className={`vcb-skip-toast ${skipToast.side === 'left' ? 'left' : 'right'}`}
          aria-hidden="true"
        >
          {skipToast.delta > 0 ? '+10s' : '−10s'}
        </div>
      )}

      {/* Mobile seek zones */}
      <div
        className="vcb-tap-zone vcb-tap-left"
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); handleTap(e, 'left'); }}
        onDoubleClick={(e) => { e.stopPropagation(); handleDoubleTap(e, 'left'); }}
        aria-hidden="true"
      />
      <div
        className="vcb-tap-zone vcb-tap-right"
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); handleTap(e, 'right'); }}
        onDoubleClick={(e) => { e.stopPropagation(); handleDoubleTap(e, 'right'); }}
        aria-hidden="true"
      />

      {/* Corner toolbars */}
      <div className="vcb-corner vcb-corner-left" aria-hidden="true" />

      {showCornerTools ? (
        <div className="vcb-corner vcb-corner-right" aria-hidden="false">
        
        <button className="vcb-btn vcb-shot-corner" onClick={onCaptureScreenshot} aria-label="Capture screenshot" title="Capture screenshot">📸</button>
        {isFullscreen && (
          <button
            className={`vcb-btn vcb-notes-corner ${showFsNotes ? 'vcb-notes-active' : ''}`}
            onClick={onToggleNotes}
            aria-pressed={showFsNotes}
            aria-label="Toggle notes panel"
            title="Toggle notes panel"
          >📝</button>
        )}
        </div>
      ) : null}

      {/* Bottom controls bar */}
      <div
        ref={rootRef}
        className="vcb-root"
        role="group"
        aria-label="Video controls"
        tabIndex={0}
        onPointerDown={(e) => { e.stopPropagation(); focusRoot(); }}
        onClick={(e) => { e.stopPropagation(); focusRoot(); }}
      >
        <div className="vcb-left">
          {isPlaying ? (
            <button className="vcb-btn" onClick={onPause} aria-label="Pause">⏸</button>
          ) : (
            <button className="vcb-btn" onClick={onPlay} aria-label="Play">▶️</button>
          )}

          {showSkipButtons ? (
            <>
              <button
                type="button"
                className="vcb-btn vcb-skip-inline"
                onClick={() => doSeek(-skipSeconds)}
                aria-label={`Back ${skipSeconds} seconds`}
                title={`Back ${skipSeconds} seconds`}
              >
                -{skipSeconds}
              </button>
              <button
                type="button"
                className="vcb-btn vcb-skip-inline"
                onClick={() => doSeek(skipSeconds)}
                aria-label={`Forward ${skipSeconds} seconds`}
                title={`Forward ${skipSeconds} seconds`}
              >
                +{skipSeconds}
              </button>
            </>
          ) : null}

          <div className="vcb-time">
            <span className="vcb-time-cur">
              {format((seeking || pendingSeek) ? seekVal : currentTime)}
            </span>
            <span className="vcb-time-sep">/</span>
            <span className="vcb-time-dur">{format(duration)}</span>
          </div>

          <input
            className={`vcb-progress ${pendingSeek ? 'vcb-progress-pending' : ''}`}
            type="range"
            min={0}
            max={Math.max(0, duration || 0)}
            step={0.1}
            value={(seeking || pendingSeek) ? seekVal : (currentTime || 0)}
            onChange={onProgressInput}
            onMouseDown={() => setSeeking(true)}
            onMouseUp={onProgressCommit}
            onTouchStart={() => setSeeking(true)}
            onTouchEnd={onProgressCommit}
            aria-label="Seek"
          />
        </div>

        <div className="vcb-right">
          {/* ✅ NEW: POV button + menu (left of screenshot corner tool) */}
          {hasPov && (
            <div
              ref={povWrapRef}
              style={{ position: 'relative', display: 'inline-block', marginRight: '0.35rem' }}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              <button
                className="vcb-btn vcb-pov-corner"
                onClick={(e) => {
                  e.stopPropagation();

                  // Delay menu toggle so dblclick can cancel it
                  clearTimeout(povClickTimerRef.current);
                  povClickTimerRef.current = setTimeout(() => {
                    setShowPovMenu((v) => !v);
                  }, 200);
                }}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  // Cancel the pending single-click menu toggle
                  clearTimeout(povClickTimerRef.current);

                  // Ensure the menu does NOT pop open
                  setShowPovMenu(false);

                  // Cycle to next POV
                  cycleToNextPov();
                }}
                aria-label="Switch POV"
                title="Switch POV (double-click to cycle)"
              >
                🎥
              </button>


              {showPovMenu && (
                <div
                  className="vcb-pov-menu vcb-pov-menu--up"
                  role="menu"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {povOptions.map((p) => {
                    const isActive = p.id === activePovId;
                    return (
                      <button
                        key={p.id}
                        role="menuitem"
                        className={`vcb-pov-item ${isActive ? 'active' : ''}`}
                        onClick={() => {
                          setShowPovMenu(false);
                          if (!isActive) onSelectPov?.(p.id);
                        }}
                        title={p.title || 'POV'}
                      >
                        {isActive ? '✓ ' : ''}{p.title || p.id.slice(0, 6)}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <button
            className="vcb-btn vcb-mute-btn"
            onClick={onMuteToggle}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? '🔇' : localVol < 0.5 ? '🔈' : '🔊'}
          </button>

          <input
            className="vcb-volume"
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={localVol}
            onChange={onVolumeInput}
            aria-label="Volume"
          />

          <div className="vcb-speed">
            <button
              className="vcb-btn"
              onClick={() => setShowSpeedMenu((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={showSpeedMenu}
              aria-label="Playback speed"
              title="Playback speed"
            >
              {playbackRate?.toFixed(2) ?? '1.00'}×
            </button>
            {showSpeedMenu && (
              <div className="vcb-speed-menu" role="menu">
                {rates.map(r => (
                  <button
                    key={r}
                    role="menuitem"
                    className={`vcb-speed-item ${playbackRate === r ? 'active' : ''}`}
                    onClick={() => { onRateChange(r); setShowSpeedMenu(false); }}
                  >
                    {r.toFixed(2)}×
                  </button>
                ))}
              </div>
            )}
          </div>

          <button className="vcb-btn vcb-shot-btn" onClick={onCaptureScreenshot} aria-label="Capture screenshot" title="Capture screenshot">📸</button>

          <div
            ref={settingsWrapRef}
            className="vcb-settings"
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <button
              className="vcb-btn vcb-settings-btn"
              onClick={() => setShowSettingsMenu(v => !v)}
              aria-haspopup="menu"
              aria-expanded={showSettingsMenu}
              aria-label="Settings"
              title="Settings"
            >
             <span className="vcb-ellipsis" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </button>

            {showSettingsMenu && (
              <div className="vcb-settings-menu" role="menu">
                {/* Volume */}
                <div className="vcb-settings-row">
                  <button
                    className="vcb-btn vcb-settings-mini"
                    onClick={onMuteToggle}
                    aria-label={isMuted ? 'Unmute' : 'Mute'}
                    title={isMuted ? 'Unmute' : 'Mute'}
                  >
                    {isMuted ? '🔇' : localVol < 0.5 ? '🔈' : '🔊'}
                  </button>

                  <input
                    className="vcb-settings-volume"
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={localVol}
                    onChange={onVolumeInput}
                    aria-label="Volume"
                  />
                </div>

                {/* Speed */}
                <div className="vcb-settings-section">
                  <div className="vcb-settings-label">Speed</div>
                  <div className="vcb-settings-speedgrid">
                    {rates.map(r => (
                      <button
                        key={r}
                        className={`vcb-speed-item ${playbackRate === r ? 'active' : ''}`}
                        onClick={() => { onRateChange(r); setShowSettingsMenu(false); }}
                      >
                        {r.toFixed(2)}×
                      </button>
                    ))}
                  </div>
                </div>

                {/* Optional: POV inside settings (if you want) */}
                {hasPov && (
                  <div className="vcb-settings-section">
                    <div className="vcb-settings-label">POV</div>
                    <div className="vcb-settings-speedgrid">
                      {povOptions.map(p => {
                        const isActive = p.id === activePovId;
                        return (
                          <button
                            key={p.id}
                            className={`vcb-speed-item ${isActive ? 'active' : ''}`}
                            onClick={() => {
                              setShowSettingsMenu(false);
                              setShowSpeedMenu(false);
                              setShowPovMenu(false);
                              if (!isActive) onSelectPov?.(p.id);
                            }}
                            title={p.title || 'POV'}
                          >
                            {isActive ? '✓ ' : ''}{p.title || p.id.slice(0, 6)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {showTrimButton ? (
            <button
              type="button"
              className={`vcb-btn vcb-trim-btn ${trimButtonActive ? 'vcb-trim-btn--active' : ''}`}
              onClick={onTrimButtonClick}
              aria-pressed={trimButtonActive}
              aria-label={trimButtonLabel}
              title={trimButtonLabel}
            >
              <span className="vcb-trim-btn__icon" aria-hidden="true">
                ✂
              </span>
              <span>{trimButtonLabel}</span>
            </button>
          ) : null}

          {showInlineNotesButton ? (
            <button
              className={`vcb-btn vcb-notes-btn ${showFsNotes ? 'vcb-notes-active' : ''}`}
              onClick={onToggleNotes}
              aria-pressed={showFsNotes}
              aria-label="Toggle notes panel"
              title="Toggle notes panel"
            >📝</button>
          ) : null}

          <button
            className="vcb-btn"
            onClick={(e) => {
              e.stopPropagation();
              onAboutToToggleFullscreen?.();
              onToggleFullscreen?.();
            }}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            {isFullscreen ? '⤢' : '⤡'}
          </button>
        </div>
      </div>
    </div>
  );
}
